import { format, isAfter, isBefore } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Platform, View, Modal } from 'react-native';
import styled from 'styled-components/native';
import SelectField from '../../../components/form/SelectField';
import Colors from '../../../constants/Colors';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import { B, Subtitle } from '../../../components/Typography';
import FlatListPicker from '../../../components/FlatlistPicker';
import { log } from '../../../tools/LogHelper';
import { stringify } from 'flatted';
import accessibilityTags from '../../../../accessibilityTags';

const FormInputView = styled.View`
  width: 100%;
`;

const StyledContainer = styled.View`
  flex-direction: row;
  width: 100%;
  height: 56px;
  margin-bottom: 12px;
`;

const StyledFormSelectContainer = styled.View<{ marginRight?: number }>`
  width: 50%;
  margin-right: ${p => (p.marginRight ? p.marginRight : 0)}px;
`;

const StyledInputText = styled(B)<{ hasValue?: boolean }>`
  font-family: ${p => (p.hasValue ? 'DMSans-Bold' : 'WorkSans-Regular')};
  font-size: 16px;
  line-height: 18px;
  opacity: ${p => (p.hasValue ? 1 : 0.3)};
`;

const StyledTimeInputView = styled.TouchableOpacity<{
  hasHelperText?: boolean;
  error?: boolean;
}>`
  min-height: auto;
  background-color: #fff;
  justify-content: center;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  height: 56px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 12 : 12)}px;
`;

const StyledPickerView = styled.View`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 10px;
  max-width: 280px;
  border-radius: 8px;
`;

const StyledIOSModalButton = styled.TouchableOpacity`
  background-color: white;
  width: 100%;
  padding-top: 10px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding-bottom: 10px;
  max-width: ${Platform.OS === 'ios' ? 350 : 280}px;
  border-radius: 8px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledModalButtonView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;

  margin-top: 32px;
  padding-bottom: 4px;
`;

const StyledModalButton = styled.TouchableOpacity`
  height: 20px;
  margin-right: 16px;
`;

const StyledTimePickerView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const ErrorText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.error};
  margin: 4px 4px 12px 4px;
`;

export type FormTimeConfig = {
  placeholder?: string;
  allowSeconds?: boolean;
  minTime?: Date;
  maxTime?: Date;
  timeIncrement?: number | undefined;
  timeZone?: string;
  localMinTime?: string;
  localMaxTime?: string;
};

export type FormTimeFieldProps<T> = FormElementProps<T, FormTimeConfig>;

const beautifyDisplayedDate = (
  hours: number,
  minutes: number,
  includeAmPm?: boolean
) => {
  return `${hours < 10 ? '0' : ''}${includeAmPm ? hours % 12 : hours}:${
    minutes < 10 ? '0' : ''
  }${minutes}${includeAmPm ? ` ${hours > 12 ? 'PM' : 'AM'}` : ''}` as any;
};

export const formatTime = (value: string) => {
  if (typeof value === 'string') {
    // eslint-disable-next-line prefer-const
    let { hours, minutes } = parseTime(value);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours >= 12 ? hours - 12 : hours;
    // convert to twelve hour time
    if (hours === 0) {
      hours = 12;
    }
    return {
      time: beautifyDisplayedDate(hours, minutes),
      ampm
    };
  }
  return { time: '', ampm: '' };
};

export const parseTime = (value: string) => {
  const [hours, minutes] = value
    .split(':')
    .map(v => (isNaN(parseInt(v)) ? -1 : +v));
  return { hours, minutes };
};

export const convertTimeTo24Hour = (time: string | null, ampm: string) => {
  if (!time) return;
  // eslint-disable-next-line prefer-const
  let { hours, minutes } = parseTime(time);
  if (typeof hours !== 'number') return;
  if (ampm === 'PM') {
    if (hours < 12) {
      hours += 12;
    }
  } else if (ampm === 'AM' && hours >= 12) {
    hours -= 12;
  }
  const value = `${hours < 10 ? '0' : ''}${hours}:${
    minutes < 10 ? '0' : ''
  }${minutes}` as any;
  return value;
};

const FormTimeField = <T extends object>({
  element,
  edit,
  disabled
}: FormTimeFieldProps<T>) => {
  const { formState, formValues, setFormValue } = useDynamicFormContext<
    T,
    FormTimeConfig
  >();
  const key = (element.dataSource as keyof T) || element.key;
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);
  const formattedTime = formatTime(formValues[key] as any);
  const [time, setTime] = useState(formattedTime.time || null);

  const [error, setError] = useState('');
  const previousValueRef = useRef(formValues[key]);
  let { minTime, maxTime } = element.config || {};
  const {
    localMinTime,
    localMaxTime,
    timeIncrement: timeIncrementConfig
  } = element.config || {};
  // fix issue if min and max times are reversed
  if (minTime && maxTime && isAfter(minTime, maxTime)) {
    const temp = minTime;
    minTime = maxTime;
    maxTime = temp;
  }

  // make sure to set the min and max times using the original local time rather than the UTC converted time
  // Example: Form builder configured in PST set from 7am to 12pm; user in MST should also see 7am to 12pm
  const convertTime = (date: Date, localTime: string) => {
    const [hours, minutes] = localTime.split(':');
    if (!hours || !minutes) return;
    date.setHours(+hours);
    date.setMinutes(+minutes);
  };
  if (minTime && localMinTime) convertTime(minTime, localMinTime);
  if (maxTime && localMaxTime) convertTime(maxTime, localMaxTime);

  const timeIncrement =
    !timeIncrementConfig || timeIncrementConfig == null
      ? 1
      : timeIncrementConfig;

  if (minTime) {
    minTime.setSeconds(0);
    minTime.setMilliseconds(0);
  }

  if (maxTime) {
    maxTime.setSeconds(0);
    maxTime.setMilliseconds(0);
  }

  useEffect(() => {
    log(stringify(formValues));
  }, [formValues]);

  // refresh value if formValues change outside of the component
  useEffect(() => {
    if (
      !focused &&
      formValues[key] &&
      previousValueRef.current !== formValues[key]
    ) {
      const formattedTime = formatTime(formValues[key] as any);
      setTime(formattedTime.time);
      setAmpm(formattedTime.ampm);
      previousValueRef.current = formValues[key];
    }
  }, [focused, formValues, key]);

  // this handled changes
  const handleChange = (time: string | null, ampm: string) => {
    if (!time) {
      setError('');
      setFormValue(key, null as any, element);
      return;
    }
    // eslint-disable-next-line prefer-const
    let { hours, minutes } = parseTime(time);
    if (hours === 0 || hours > 12) {
      setError('Invalid Time');
      setFormValue(key, null as any, element);
      return;
    }
    if (minutes === undefined || minutes > 60 || minutes < 0) {
      setError('Invalid Time');
      setFormValue(key, null as any, element);
      return;
    }
    if (!ampm && hours < 12) {
      ampm = 'AM';
      setAmpm(ampm);
    } else if (!ampm) {
      ampm = 'PM';
      setAmpm(ampm);
    }
    if (typeof hours !== 'number') return;
    if (ampm === 'PM') {
      if (hours < 12) {
        hours += 12;
      }
    } else if (ampm === 'AM' && hours >= 12) {
      hours -= 12;
    }

    if (maxTime) {
      const newMaxTime = new Date(maxTime);
      newMaxTime.setHours(hours, minutes, 0, 0);
      if (isAfter(newMaxTime, maxTime)) {
        setError(
          `Time must not be later than ${beautifyDisplayedDate(
            maxTime.getHours(),
            maxTime.getMinutes(),
            true
          )}`
        );
        setTime(undefined);
        setFormValue(key, null as any, element);
        return;
      }
    }
    if (minTime) {
      const newMinTime = new Date(minTime);
      newMinTime.setHours(hours, minutes, 0, 0);
      if (isBefore(newMinTime, minTime)) {
        setError(
          `Time must not be earlier than ${beautifyDisplayedDate(
            minTime.getHours(),
            minTime.getMinutes(),
            true
          )}`
        );
        setTime(undefined);
        setFormValue(key, null as any, element);
        return;
      }
    }

    const value = beautifyDisplayedDate(hours, minutes);
    setError('');
    setFormValue(key, value as any, element);
  };

  const getTimeLabel = () => {
    let label: string | undefined = undefined;
    if (formValues[key]) {
      const { hours, minutes } = parseTime(String(formValues[key]));
      const minLabel =
        String(minutes).length === 1 ? '0' + String(minutes) : String(minutes);
      label =
        String(((hours + 11) % 12) + 1) +
        ':' +
        minLabel +
        (hours >= 12 ? ' PM' : ' AM');
    }
    return label;
  };

  const getInitialTime = () => {
    if (formValues[key]) {
      const { hours, minutes } = parseTime(String(formValues[key]));
      const minLabel =
        String(minutes).length === 1 ? '0' + String(minutes) : String(minutes);
      return {
        hour: String(((hours + 11) % 12) + 1),
        min: minLabel,
        timeOfDay: hours >= 12 ? 'PM' : 'AM'
      };
    }
    return undefined;
  };

  const [input, setInput] = useState(
    getInitialTime() ||
      (minTime
        ? {
            hour: `${minTime.getHours()}`,
            min: `${minTime.getMinutes()}`,
            timeOfDay: minTime.getHours() < 12 ? 'AM' : 'PM'
          }
        : { hour: '12', min: '00', timeOfDay: 'AM' })
  );
  const [showPicker, setShowPicker] = useState(false);

  const isTimeValid = useCallback(
    (hour: number, minutes: number) => {
      if (maxTime) {
        const newMaxTime = new Date(maxTime);
        newMaxTime.setHours(Number(hour), minutes, 0, 0);
        if (isAfter(newMaxTime, maxTime)) {
          return false;
        }
      }
      if (minTime) {
        const newMinTime = new Date(minTime);
        newMinTime.setHours(Number(hour), minutes, 0, 0);
        if (isBefore(newMinTime, minTime)) {
          return false;
        }
      }
      return true;
    },
    [maxTime, minTime]
  );

  const amPmOptions = useMemo(() => {
    let options = ['AM', 'PM'];
    const minTimeHour =
      minTime?.getHours() !== undefined ? minTime?.getHours() : 0;
    const maxTimeHour =
      maxTime?.getHours() !== undefined ? maxTime?.getHours() : 23;
    if (minTimeHour >= 12) {
      options = ['PM'];
    } else if (maxTimeHour < 12) {
      options = ['AM'];
    }
    return options;
  }, [maxTime, minTime]);

  const [ampm, setAmpm] = useState(formattedTime.ampm || amPmOptions[0]);

  const hourOptions = useMemo(() => {
    const defaultOptions = [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12'
    ];
    const options = defaultOptions.filter(data => {
      const hour12 = Number(data) === 12 ? 0 : Number(data);
      const hour24 = hour12 < 12 ? hour12 + 12 : hour12;
      const hour =
        (Platform.OS === 'web' ? ampm : input.timeOfDay) === 'AM'
          ? hour12
          : hour24;
      const minTimeHour =
        minTime?.getHours() !== undefined ? minTime?.getHours() : 0;
      const maxTimeHour =
        maxTime?.getHours() !== undefined ? maxTime?.getHours() : 23;
      return hour >= minTimeHour && hour <= maxTimeHour;
    });
    if (options.length === 0) return defaultOptions;
    return options;
  }, [ampm, input.timeOfDay, minTime, maxTime]);
  const minuteOptions = useMemo(() => {
    const options: string[] = [];
    const hour =
      input.timeOfDay === 'AM'
        ? Number(input.hour) === 12
          ? 0
          : Number(input.hour)
        : Number(input.hour) < 12
        ? Number(input.hour) + 12
        : 12;
    for (let i = 0; i < 60; i += timeIncrement) {
      if (isTimeValid(hour, i)) {
        options.push(`${i < 10 ? '0' : ''}${i}`);
      }
    }
    return options;
  }, [timeIncrement, input.hour, input.timeOfDay, isTimeValid]);

  const webTimeOptions = useMemo(() => {
    const options: string[] = [];
    hourOptions.forEach(h => {
      const hour =
        ampm == 'AM'
          ? Number(h) === 12
            ? 0
            : Number(h)
          : Number(h) < 12
          ? Number(h) + 12
          : 12;
      for (let x = 0; x < 60; x += timeIncrement) {
        if (isTimeValid(hour, x)) {
          options.push(
            (hour % 12 === 0 ? 12 : hour % 12) +
              ':' +
              `${x < 10 ? '0' : ''}${x}`
          );
        }
      }
    });
    return options;
  }, [hourOptions, ampm, timeIncrement, isTimeValid]);

  return (
    <View>
      <FormElementQuestion element={element} />
      <FormInputView accessibilityLabel={accessibilityTags.timeID} testID={accessibilityTags.timeID} >
        <StyledContainer>
          {Platform.OS !== 'web' ? (
            <StyledTimeInputView
              onPress={() => {
                setShowPicker(true);
              }}
            >
              <StyledInputText color='dark' hasValue={Boolean(formValues[key])}>
                {formValues[key] ? getTimeLabel() : 'HH:MM aa'}
              </StyledInputText>
            </StyledTimeInputView>
          ) : (
            <StyledContainer>
              <StyledFormSelectContainer marginRight={8}>
                <SelectField
                  value={time ? String(time).replace(/^0+/, '') : undefined}
                  placeholder='HH:MM'
                  options={webTimeOptions}
                  onChange={option => {
                    const value =
                      typeof option === 'string' ? option : option.value;
                    setTime(value as string);
                    handleChange(value as string, ampm);
                  }}
                />
              </StyledFormSelectContainer>
              <StyledFormSelectContainer>
                <SelectField
                  value={ampm}
                  placeholder='AM/PM'
                  options={amPmOptions}
                  onChange={option => {
                    const value =
                      typeof option === 'string' ? option : option.value;
                    setAmpm(value as string);
                    handleChange(time, value as string);
                  }}
                />
              </StyledFormSelectContainer>
            </StyledContainer>
          )}
        </StyledContainer>
        {Boolean(error) && <ErrorText>{error}</ErrorText>}
      </FormInputView>
      <Modal
        visible={showPicker && Platform.OS !== 'web'}
        transparent={true}
        animationType='fade'
      >
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowPicker(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>

          <StyledPickerView>
            <StyledTimePickerView>
              <FlatListPicker
                onValueChanged={data => {
                  setInput({ ...input, hour: data });
                }}
                initialValue={input.hour}
                options={hourOptions.map(
                  opts => `${Number(opts) === 12 ? 12 : Number(opts) % 12}`
                )}
              />
              <Subtitle color='dark'>:</Subtitle>
              <FlatListPicker
                onValueChanged={data => {
                  setInput({ ...input, min: data });
                }}
                initialValue={input.min}
                options={minuteOptions}
              />
              <FlatListPicker
                onValueChanged={data => {
                  setInput({ ...input, timeOfDay: data });
                }}
                initialValue={input.timeOfDay}
                options={amPmOptions}
              />
            </StyledTimePickerView>
            <StyledModalButtonView>
              <StyledModalButton
                onPress={() => {
                  setShowPicker(false);
                }}
              >
                <B color='dark'>Cancel</B>
              </StyledModalButton>
              <StyledModalButton
                onPress={() => {
                  setShowPicker(false);
                  handleChange(input.hour + ':' + input.min, input.timeOfDay);
                }}
              >
                <B accessibilityLabel={accessibilityTags.confirmButtonID} testID={accessibilityTags.confirmButtonID}>Confirm</B>
                </StyledModalButton>
            </StyledModalButtonView>
          </StyledPickerView>
        </StyledModalView>
      </Modal>
    </View>
  );
};

export const FormTimeFieldPlugin: FormElementPlugin<any, FormTimeConfig> = {
  name: 'Time',
  type: 'Time',
  dataTypes: ['time'],
  element: FormTimeField
};
